import { Box, Grid } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { END_POINTS } from "constants/APIConstant";
import fetch from "auth/FetchInterceptor";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import CircularProgress from "@mui/material/CircularProgress";
import SnackbarModal from "component/shared/snackbar";
import ReactGA from "react-ga4";
import { useDispatch, useSelector } from "react-redux";
import { apiResponseMessage } from "redux/actions/ApiMessageResponse";
import { MESSAGE_TYPE } from "redux/constants/ApiMessageResponse";
import { extractAxiosErrorMessage } from "utils/extractApiErrorMessage";

export default function ForgetPassword() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dir = useSelector(({ theme }) => theme.direction);

  const { id } = useParams();

  ReactGA.initialize(END_POINTS.TRACKING_ID);

  useEffect(() => {
    // Send pageview with a custom path
    ReactGA.send({
      hitType: "pageview",
      page: `/forgetPass/${id}`,
      title: `ForgetPass page`,
    });
  }, [id]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => {
      clearTimeout(timeOut);
    };
  }, [id]);

  const [message, setMessage] = useState(null);
  const [loadingBtn, setLoadingBtn] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // feedback
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // error
  const [openError, setOpenError] = useState(false);
  const handleOpenError = () => {
    setOpenError(true);
  };
  const handleCloseError = () => {
    setOpenError(false);
  };

  const [forgetPass, setForgetPass] = useState({
    token: id,
    newPassword: "",
    confirmPassword: "",
  });

  const handleForgetPass = (e) => {
    let myPass = { ...forgetPass };
    myPass[e.target.name] = e.target.value;
    setForgetPass(myPass);
  };

  async function forgetPassForm(e) {
    e.preventDefault();
    setLoadingBtn(true);

    let passValidate = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W,_]).{8,32}$/;

    if (
      forgetPass.newPassword.match(passValidate) &&
      forgetPass.confirmPassword.match(passValidate) &&
      forgetPass.newPassword === forgetPass.confirmPassword
    ) {
      await fetch
        .post(`${END_POINTS.EXECUTE_FORGOT_PASS}`, {
          ...forgetPass,
          confirmPassword: null,
        })
        .then((res) => {
          if (res.state === true) {
            dispatch(
              apiResponseMessage({
                showMessage: true,
                message: res.message || (
                  <FormattedMessage id="sent-successfully" />
                ),
                messageType: MESSAGE_TYPE.SUCCESS,
              })
            );
            setLoadingBtn(false);
            setTimeout(() => {
              handleClose();
              navigate(`${APP_PREFIX_PATH}/login`);
            }, 1000);
          }
        })
        .catch((e) => {
          dispatch(
            apiResponseMessage({
              showMessage: true,
              message: extractAxiosErrorMessage(e),
              messageType: MESSAGE_TYPE.ERROR,
            })
          );
          setLoadingBtn(false);
          setTimeout(() => {
            handleCloseError();
          }, 10000);
        });
    } else {
      setLoadingBtn(false);
      if (
        !forgetPass.newPassword.match(passValidate) ||
        !forgetPass.confirmPassword.match(passValidate)
      ) {
        dispatch(
          apiResponseMessage({
            showMessage: true,
            message: <FormattedMessage id="password-validation" />,
            messageType: MESSAGE_TYPE.ERROR,
          })
        );
        setTimeout(() => {
          handleCloseError();
        }, 10000);
      } else if (forgetPass.newPassword !== forgetPass.confirmPassword) {
        dispatch(
          apiResponseMessage({
            showMessage: true,
            message: <FormattedMessage id="passwordMatch" />,
            messageType: MESSAGE_TYPE.ERROR,
          })
        );
        setTimeout(() => {
          handleCloseError();
        }, 10000);
      }
    }
  }

  return (
    <div className="d-flex align-items-center flex-column py-5">
      <Grid item xs={12} sx={{ textAlign: "center", mb: 3 }}>
        <h2 style={{ color: "var(--main-color)" }}>
          <FormattedMessage id="resetPass" />
        </h2>
      </Grid>
      <Box
        sx={{
          border: "1px solid #E8E8E8",
          borderRadius: "0.75rem",
          width: "50%",
          minWidth: "300px",
        }}
        className={"shadow"}
      >
        <div className="p-5">
          <form onSubmit={forgetPassForm}>
            <div className="mb-3">
              <label className="mb-2 fw-bold" htmlFor={"newPassword"}>
                {intl.formatMessage({ id: "password" })}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  onChange={handleForgetPass}
                  className="bg-transparent form-control fw-bold mx-auto py-2"
                  style={{ fontSize: "14px" }}
                  required
                  name={"newPassword"}
                  id={"newPassword"}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: dir === "rtl" ? "auto" : "20px",
                    left: dir === "rtl" ? "20px" : "auto",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "16px",
                    color: "#666",
                  }}
                >
                  {showPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>
            <div className="mb-4">
              <label className="mb-2 fw-bold" htmlFor={"confirmPassword"}>
                {intl.formatMessage({ id: "reenter-password" })}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  onChange={handleForgetPass}
                  className="bg-transparent form-control fw-bold mx-auto py-2"
                  style={{ fontSize: "14px" }}
                  required
                  name={"confirmPassword"}
                  id={"confirmPassword"}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: dir === "rtl" ? "auto" : "20px",
                    left: dir === "rtl" ? "20px" : "auto",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "16px",
                    color: "#666",
                  }}
                >
                  {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>
            <div className="text-center">
              <button type="submit" className="main-btn mx-auto">
                <span>
                  {loadingBtn ? (
                    <CircularProgress sx={{ color: "#fff" }} size={26} />
                  ) : (
                    intl.formatMessage({ id: "confirm" })
                  )}
                </span>
              </button>
            </div>
          </form>
        </div>
      </Box>

      {/* feedback */}
      <SnackbarModal
        open={open}
        onClose={handleClose}
        severity="success"
        message={message}
      />

      {/* error */}
      <SnackbarModal
        open={openError}
        onClose={handleCloseError}
        severity="error"
        message={message}
      />
    </div>
  );
}
